'use client';

export interface PriceBand {
  id: string;     // "25-50"
  label: string;  // "£25–£50"
  min: number;    // 25
  max: number;    // 50 (Infinity for "Over")
}

export const PRICE_BANDS: PriceBand[] = [
  { id: '0-25', label: 'Under £25', min: 0, max: 25 },
  { id: '25-50', label: '£25–£50', min: 25, max: 50 },
  { id: '50-100', label: '£50–£100', min: 50, max: 100 },
  { id: '100-200', label: '£100–£200', min: 100, max: 200 },
  { id: '200+', label: 'Over £200', min: 200, max: Infinity },
];

export interface FilterState {
  price: string[];
  brands: string[];
  attributes: Record<string, string[]>;
  inStockOnly: boolean;
}

export interface AttributeFilterOption {
  id: string;
  label: string;
}

export interface AttributeFilterFacet {
  id: string;
  label: string;
  options: AttributeFilterOption[];
}

export interface FilterSidebarProps {
  state: FilterState;
  togglePrice: (id: string) => void;
  toggleBrand: (brand: string) => void;
  toggleStock: () => void;
  clearAll: () => void;
  brandCounts: Map<string, number>;
  priceCounts: Map<string, number>;
  attributeFacets?: AttributeFilterFacet[];
  attributeCounts?: Map<string, Map<string, number>>;
  stockCount: number;
  allBrands: string[];
  activeCount: number;
  toggleAttribute?: (facetId: string, optionId: string) => void;
}

export default function FilterSidebar({
  state,
  togglePrice,
  toggleBrand,
  toggleStock,
  clearAll,
  brandCounts,
  priceCounts,
  attributeFacets = [],
  attributeCounts = new Map(),
  stockCount,
  allBrands,
  activeCount,
  toggleAttribute,
}: FilterSidebarProps) {
  return (
    <div className="flt">
      <div className="flt-head">
        <h2 className="flt-title">Filters</h2>
        {activeCount > 0 && (
          <button type="button" className="flt-clear" onClick={clearAll}>
            Clear all
          </button>
        )}
      </div>

      <fieldset className="flt-group">
        <legend className="flt-group-label">Price</legend>
        {PRICE_BANDS.map((band) => {
          const count = priceCounts.get(band.id) ?? 0;
          const disabled = count === 0;
          const checked = state.price.includes(band.id);
          return (
            <label
              key={band.id}
              className={`flt-opt ${disabled && !checked ? 'flt-opt-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => togglePrice(band.id)}
                disabled={disabled && !checked}
              />
              <span className="flt-opt-box" aria-hidden>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              <span className="flt-opt-label">{band.label}</span>
              <span className="flt-opt-count">({count})</span>
            </label>
          );
        })}
      </fieldset>

      {allBrands.length > 0 && (
        <fieldset className="flt-group">
          <legend className="flt-group-label">Brand</legend>
          <div className="flt-group-scroll">
            {allBrands.map((brand) => {
              const count = brandCounts.get(brand) ?? 0;
              const disabled = count === 0;
              const checked = state.brands.includes(brand);
              return (
                <label
                  key={brand}
                  className={`flt-opt ${disabled && !checked ? 'flt-opt-disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleBrand(brand)}
                    disabled={disabled && !checked}
                  />
                  <span className="flt-opt-box" aria-hidden>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <span className="flt-opt-label">{brand}</span>
                  <span className="flt-opt-count">({count})</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {attributeFacets.map((facet) => (
        <fieldset key={facet.id} className="flt-group">
          <legend className="flt-group-label">{facet.label}</legend>
          <div className="flt-group-scroll">
            {facet.options.map((option) => {
              const count = attributeCounts.get(facet.id)?.get(option.id) ?? 0;
              const disabled = count === 0;
              const checked = state.attributes[facet.id]?.includes(option.id) ?? false;
              return (
                <label
                  key={option.id}
                  className={`flt-opt ${disabled && !checked ? 'flt-opt-disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAttribute?.(facet.id, option.id)}
                    disabled={disabled && !checked}
                  />
                  <span className="flt-opt-box" aria-hidden>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <span className="flt-opt-label">{option.label}</span>
                  <span className="flt-opt-count">({count})</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}

      <fieldset className="flt-group">
        <legend className="flt-group-label">Availability</legend>
        <label className="flt-opt">
          <input
            type="checkbox"
            checked={state.inStockOnly}
            onChange={toggleStock}
          />
          <span className="flt-opt-box" aria-hidden>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <span className="flt-opt-label">In stock only</span>
          <span className="flt-opt-count">({stockCount})</span>
        </label>
      </fieldset>

      <style>{`
        .flt {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .flt-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid var(--color-border);
        }
        .flt-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0;
        }
        .flt-clear {
          background: transparent;
          border: none;
          color: var(--color-primary);
          font-size: 0.8rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          padding: 0;
        }
        .flt-clear:hover { text-decoration: underline; }

        .flt-group {
          border: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }
        .flt-group-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 0;
          margin: 0 0 0.25rem;
        }
        .flt-group-scroll {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          max-height: 240px;
          overflow-y: auto;
          padding-right: 0.25rem;
        }

        .flt-opt {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          cursor: pointer;
          font-size: 0.88rem;
          color: var(--color-text-primary);
          line-height: 1.3;
        }
        .flt-opt input[type="checkbox"] {
          position: absolute;
          width: 1px;
          height: 1px;
          opacity: 0;
          pointer-events: none;
        }
        .flt-opt-box {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          border: 1.5px solid var(--color-border-dark);
          border-radius: 0.3rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: var(--color-white);
          color: transparent;
          transition: background 0.12s, border-color 0.12s, color 0.12s;
        }
        .flt-opt input:checked + .flt-opt-box {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: var(--color-white);
        }
        .flt-opt input:focus-visible + .flt-opt-box {
          box-shadow: 0 0 0 3px rgba(27,63,35,0.18);
        }
        .flt-opt-label {
          flex: 1;
          min-width: 0;
        }
        .flt-opt-count {
          font-size: 0.78rem;
          color: var(--color-text-muted);
          flex-shrink: 0;
        }
        .flt-opt-disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
